import { Component, OnInit, OnDestroy } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil, switchMap } from 'rxjs/operators';
import { FuseSearchLauncherService } from '../search-launcher.service';

@Component({
    selector: 'spotlight-dialog',
    templateUrl: './spotlight-dialog.component.html',
    styleUrls: ['./spotlight-dialog.component.scss']
})
export class SpotLightDialogComponent implements OnInit, OnDestroy {
    searchControl: FormControl = new FormControl('');
    searchResults: any[] = [];
    isLoading: boolean = false;
    selectedResultIndex: number = -1;
    private _unsubscribeAll: Subject<void> = new Subject<void>();

    activeModule: string = 'applicant';
    availableModules: string[] = [];

    setActiveModule(module: string): void {
        this.activeModule = 'applicant';
        // Call searchAnything API with current search value
        if (this.searchControl.value) {
            this.isLoading = true;
            const searchObs = this.activeModule === 'applicant'
                ? this._fuseSearchLauncherService.semanticSearch(this.searchControl.value)
                : this._fuseSearchLauncherService.searchAnything(this.activeModule, this.searchControl.value);

            searchObs.pipe(takeUntil(this._unsubscribeAll))
                .subscribe((res: any) => {
                    this.searchResults = this.activeModule === 'applicant' ? this.mapSemanticResults(res) : res;
                    this.isLoading = false;
                }, () => {
                    this.isLoading = false;
                });
        }
    }

    clearSearch(): void {
        this.searchControl.setValue('');
        this.searchResults = [];
    }

    constructor(
        private _dialogRef: MatDialogRef<SpotLightDialogComponent>,
        private _fuseSearchLauncherService: FuseSearchLauncherService
    ) {
        // Handle keyboard events
        this._dialogRef.keydownEvents().subscribe(event => {
            if (event.key === 'Escape') {
                this._dialogRef.close();
            } else if (event.key === 'ArrowDown') {
                event.preventDefault();
                this.navigateResults(1);
            } else if (event.key === 'ArrowUp') {
                event.preventDefault();
                this.navigateResults(-1);
            } else if (event.key === 'Enter') {
                event.preventDefault();
                if (this.selectedResultIndex !== -1 && this.searchResults[this.selectedResultIndex]) {
                    this.selectResult(this.searchResults[this.selectedResultIndex]);
                }
            }
        });
    }

    navigateResults(direction: number): void {
        if (this.searchResults.length === 0) return;
        this.selectedResultIndex = (this.selectedResultIndex + direction + this.searchResults.length) % this.searchResults.length;
        this.scrollToSelected();
    }

    scrollToSelected(): void {
        setTimeout(() => {
            const selectedElement = document.querySelector('.selected-result');
            if (selectedElement) {
                selectedElement.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
            }
        });
    }

    selectResult(result: any): void {
        if (result?.url) {
            window.location.href = result.url;
            this._dialogRef.close();
        }
    }

    ngOnInit(): void {
        // Subscribe to search input changes
        this.searchControl.valueChanges
            .pipe(
                debounceTime(300),
                distinctUntilChanged(),
                takeUntil(this._unsubscribeAll),
                switchMap((query) => {
                    if (!query) {
                        this.searchResults = [];
                        this.isLoading = false;
                        return [];
                    }
                    this.isLoading = true;
                    if (this.activeModule === 'applicant') {
                        return this._fuseSearchLauncherService.semanticSearch(query);
                    }
                    return this._fuseSearchLauncherService.searchAnything(this.activeModule, query);
                })
            )
            .subscribe((res: any) => {
                const query = this.searchControl.value.toLowerCase();

                // If query is 'demo', inject rich semantic dummy data (kept for testing)
                if (query.includes('demo')) {
                    this.searchResults = this.getSemanticDummyData(this.searchControl.value);
                } else {
                    this.searchResults = this.activeModule === 'applicant' ? this.mapSemanticResults(res) : res;
                }

                this.isLoading = false;
                this.selectedResultIndex = this.searchResults.length > 0 ? 0 : -1;
            }, () => {
                this.isLoading = false;
            });
    }

    private getSemanticDummyData(query: string): any[] {
        const data = [
            {
                text: 'Mansoor Ahmad',
                url: '/applicants/1',
                module: 'applicant',
                score: 0.98,
                item: {
                    status: 'Active',
                    branch: { name: 'Main Branch' }
                },
                description: 'Experienced Senior Frontend Engineer with over 8 years of experience in Angular, React, and Node.js. Proficient in building large-scale enterprise applications with complex state management.',
                skills: ['Angular', 'TypeScript', 'SCSS', 'System Architecture', 'Node.js'],
                location: 'Berlin, Germany',
                matchReason: 'Highly relevant match for "' + query + '". Resume mentions deep expertise in Angular and enterprise-grade architecture.'
            },
            {
                text: 'John Doe',
                url: '/applicants/2',
                module: 'applicant',
                score: 0.85,
                item: {
                    status: 'In Review',
                    branch: { name: 'Tech Division' }
                },
                description: 'Software developer focused on Full Stack Development. Strong skills in React and Cloud Services (AWS). Has worked on several vector search projects recently.',
                skills: ['React', 'AWS', 'Python', 'Elasticsearch'],
                location: 'London, UK',
                matchReason: 'Strong match. Mention of vector search projects directly aligns with your current focus on AI search integration.'
            },
            {
                text: 'Sarah Smith',
                url: '/applicants/3',
                module: 'applicant',
                score: 0.72,
                item: {
                    status: 'Hired',
                    branch: { name: 'HR Dept' }
                },
                description: 'Background in Product Management but with strong technical foundation. Skilled in Agile methodologies and cross-functional leadership.',
                skills: ['Product Management', 'Agile', 'JIRA'],
                location: 'New York, USA',
                matchReason: 'Matches semantic context of "' + query + '" and leadership, though not a direct technical match.'
            }
        ];

        return data.map(item => ({
            ...item,
            highlight: this.getHighlight(item.description, query)
        }));
    }

    private getHighlight(text: string, query: string): string {
        if (!query || !text) return text;

        // Escape special regex characters
        const escapedQuery = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const regex = new RegExp(`(${escapedQuery})`, 'gi');

        return text.replace(regex, '<b>$1</b>');
    }

    private mapSemanticResults(results: any[]): any[] {
        if (!results || !Array.isArray(results)) return [];

        return results.map(res => ({
            text: res.name || 'Unknown Applicant',
            url: `/applicants/edit/${res._id}`,
            module: 'applicant',
            item: {
                status: res.workStatus || 'Active'
            },
            // Normalize RRF score (max is ~0.032 for k=60, rank=1). 
            // Let's scale it for the UI (this is just for visual representation)
            score: Math.min(res.score * 30, 0.99),
            description: res.highlight?.text?.[0] || res.summary || 'No summary available',
            skills: res.skills ? res.skills.split(',').map((s: string) => s.trim()) : [],
            location: res.location || 'N/A',
            matchReason: 'Hybrid result with a relevance score of ' + (res.score * 1000).toFixed(1) + '.',
            highlight: res.highlight?.text?.[0]?.replace(/\[HL\]/g, '<b>').replace(/\[\/HL\]/g, '</b>')
        }));
    }


    ngOnDestroy(): void {
        this._unsubscribeAll.next();
        this._unsubscribeAll.complete();
    }
}